import apiClient from "./axios";
import { authFetch } from "./authFetch";

/**
 * 结算管理相关API
 */
export const settlementApi = {
    /**
     * 航段费用管理
     */
    legCost: {
        // 获取航段费用列表
        getLegCostList: (params) => apiClient.get("/v1/settlement/leg-costs", { params }),

        // 获取单个航段费用详情
        getLegCostDetail: (id) => apiClient.get(`/v1/settlement/leg-costs/${id}`),

        // 更新航段费用
        updateLegCost: (id, data) => apiClient.put(`/v1/settlement/leg-costs/${id}`, data),

        // 批量更新航段费用
        batchUpdateLegCosts: (data) => apiClient.post("/v1/settlement/leg-costs/batch", data),

        // 批量删除航段费用(软删除,需删除原因;ids 可为航段 id 整数=按航段全删,或费项 UUID=选费项删)
        batchDeleteLegCosts: (ids, deleteReason) =>
            apiClient.delete("/v1/settlement/leg-costs/batch", {
                data: { ids, delete_reason: deleteReason },
            }),

        // 按航段列表拉取这批航段下所有 active 费项清单(供"批量删除→选费项删除"勾选)
        getLegCostItemsByFlights: (flightOperationIds) =>
            apiClient.post("/v1/settlement/leg-costs/items-by-flights", {
                flight_operation_ids: flightOperationIds,
            }),

        // 下载导入模板
        downloadTemplate: () =>
            apiClient.get("/v1/settlement/leg-cost-template", {
                responseType: "blob",
                _skipInterceptorProcessing: true, // 跳过响应拦截器处理，避免破坏blob数据
            }),

        // 校验Excel文件（第一阶段）
        validateLegCostFile: async (file) => {
            const formData = new FormData();
            formData.append("file", file);

            // 使用原生fetch API，绕过axios拦截器
            // 因为axios拦截器会破坏FormData对象
            try {
                const response = await authFetch(`${apiClient.defaults.baseURL}/v1/settlement/leg-cost-validate`, {
                    method: "POST",
                    body: formData,
                }, { entrypoint: "settlement-leg-cost-validate" });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                // 返回axios风格的响应格式，保持兼容性
                return { data };
            } catch (error) {
                console.error("❌ 文件上传失败:", error);
                throw error;
            }
        },

        // 确认导入（第二阶段，原版本不带重复检测）
        confirmLegCostImport: (data) => apiClient.post("/v1/settlement/leg-cost-import-confirm", data),

        // 预检导入（只读，返回新增/匹配数量统计）
        precheckLegCostImport: (validRecords) => {
            const formData = new FormData();
            formData.append("valid_records", JSON.stringify(validRecords));

            return apiClient.post("/v1/settlement/leg-cost-import-precheck", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
        },

        // 确认导入（带重复检测和覆盖/叠加处理）
        confirmLegCostImportWithDuplicateHandling: (validRecords, file, importMode = "overwrite") => {
            const formData = new FormData();
            formData.append("valid_records", JSON.stringify(validRecords));
            formData.append("file", file);
            formData.append("import_mode", importMode);

            return apiClient.post("/v1/settlement/leg-cost-import-confirm-with-duplicate-handling", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
        },

        // 导入Excel（旧接口，保持兼容）
        importLegCosts: (file) => {
            const formData = new FormData();
            formData.append("file", file);
            return apiClient.post("/v1/settlement/leg-costs/import", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
        },

        // 导出Excel
        exportLegCosts: (params) =>
            apiClient.get("/v1/settlement/leg-costs/export", {
                params,
                responseType: "blob",
            }),

        // 获取费用明细
        getCostDetail: (legCostId, costType) => apiClient.get(`/v1/settlement/leg-costs/${legCostId}/details/${costType}`),

        // 手动录入单条航段费用
        createManualLegCost: (data) => apiClient.post("/v1/settlement/leg-costs/manual", data),

        // 手动录入前预检:检测同航段+费用类型+供应商是否已存在(只读)
        precheckManualLegCost: (data) => apiClient.post("/v1/settlement/leg-costs/manual/precheck", data),

        // 获取费用记录的变更历史
        getLineItemChangeHistory: (lineItemId) => apiClient.get(`/v1/settlement/line-items/${lineItemId}/history`),
    },

    /**
     * 航班费用管理（待开发）
     */
    flightCost: {
        // 获取航班费用列表
        getFlightCostList: (params) => apiClient.get("/v1/settlement/flight-costs", { params }),

        // 获取航班费用详情
        getFlightCostDetail: (id) => apiClient.get(`/v1/settlement/flight-costs/${id}`),
    },

    /**
     * 公共费用管理
     */
    publicCost: {
        // 获取公共费用列表
        getPublicCostList: (params) => apiClient.get("/v1/settlement/public-costs", { params }),

        // 分配公共费用
        allocatePublicCost: (data) => apiClient.post("/v1/settlement/public-costs/allocate", data),
    },

    /**
     * 销售航线管理
     */
    salesRoutes: {
        // 获取销售航线列表
        getSalesRoutes: (params) => apiClient.get("/v1/settlement/sales-routes", { params }),

        // 获取单个销售航线详情
        getSalesRoute: (id) => apiClient.get(`/v1/settlement/sales-routes/${id}`),

        // 创建销售航线
        createSalesRoute: (data) => apiClient.post("/v1/settlement/sales-routes", data),

        // 更新销售航线
        updateSalesRoute: (id, data) => apiClient.put(`/v1/settlement/sales-routes/${id}`, data),

        // 删除销售航线
        deleteSalesRoute: (id) => apiClient.delete(`/v1/settlement/sales-routes/${id}`),
    },

    /**
     * 批次核算管理 (v2.0)
     */
    batchCalculations: {
        // 获取批次待处理数量（轻量接口，用于侧边栏红点）
        getPendingCount: () => apiClient.get("/v1/settlement/batch-calculations/pending-count"),

        // 创建并执行批次核算
        createBatchCalculation: (data) => apiClient.post("/v1/settlement/batch-calculations", data),

        // 获取批次列表（分页）
        getBatchCalculations: (params) => apiClient.get("/v1/settlement/batch-calculations", { params }),

        // 获取批次详情
        getBatchCalculation: (id) => apiClient.get(`/v1/settlement/batch-calculations/${id}`),

        // 获取批次状态（用于轮询）
        getBatchCalculationStatus: (id) => apiClient.get(`/v1/settlement/batch-calculations/${id}/status`),

        // 获取批次异常报告
        getBatchAnomalies: (id, params) => apiClient.get(`/v1/settlement/batch-calculations/${id}/anomalies`, { params }),

        // 刷新批次（重新计算）
        refreshBatchCalculation: (id) => apiClient.put(`/v1/settlement/batch-calculations/${id}/refresh`),

        // 删除批次
        deleteBatchCalculation: (id) => apiClient.delete(`/v1/settlement/batch-calculations/${id}`),
    },

    /**
     * 结算账单数据可信化(录入员侧)— 全部走 /api/v3/settlement/
     *
     * 规格:`docs/fzx_doc/结算/结算账单数据可信化-录入员侧/结算账单数据可信化-录入员侧_功能规格说明书.md`
     * 注意:本命名空间下接口受 `settlement:exception:read` / `settlement:exception:write`
     *      / `settlement:rule:admin` 权限保护。前端在调用前应保证用户已登录。
     */
    dataQuality: {
        // T15 — 批次未识别费用清单
        getBatchUnrecognizedCosts: (batchId, { page = 1, pageSize = 50 } = {}) =>
            apiClient.get(`/v3/settlement/batches/${batchId}/unrecognized-costs`, {
                params: { page, page_size: pageSize },
            }),

        // T17 / T18 — 航段费用明细下钻
        getLegCostDetails: (flightOperationId, costType, { batchId } = {}) =>
            apiClient.get(
                `/v3/settlement/leg-costs/${flightOperationId}/details/${costType}`,
                { params: batchId ? { batch_id: batchId } : {} }
            ),

        // T19 — 账单行项目原件签名 URL
        getInvoiceLineItemPreviewUrl: (invoiceLineItemId, { expiresIn = 300 } = {}) =>
            apiClient.get(
                `/v3/settlement/invoice-line-items/${invoiceLineItemId}/preview-url`,
                { params: { expires_in: expiresIn } }
            ),

        // T19 配套 — 账单数据源原件签名 URL(供 FrontendImportModal 在 PARSE_FAILED 后用)
        getInvoiceDataSourcePreviewUrl: (dataSourceId, { expiresIn = 300 } = {}) =>
            apiClient.get(
                `/v3/settlement/invoice-data-sources/${dataSourceId}/preview-url`,
                { params: { expires_in: expiresIn } }
            ),

        // T24a — 立即重算批次
        // 注:规格写的是 POST /profit-reports/{id}/recompute,实际"重算"的实体是批次。
        // 后端实现为 POST /v3/settlement/batches/{batchId}/recompute,前端走该路径。
        recomputeBatch: (batchId) =>
            apiClient.post(`/v3/settlement/batches/${batchId}/recompute`),

        // T26b — 软删除单条账单行项目(配套 T50a 软删除字段)
        softDeleteInvoiceLineItem: (invoiceLineItemId, deleteReason) =>
            apiClient.delete(
                `/v3/settlement/invoice-line-items/${invoiceLineItemId}`,
                { data: { delete_reason: deleteReason } }
            ),

        removeAllocatedShare: (allocationItemId, deleteReason) =>
            apiClient.delete(
                `/v3/settlement/allocation-items/${allocationItemId}`,
                { data: { delete_reason: deleteReason } }
            ),

        // ------------------------------------------------------------
        // S3 数据异常工作台(T35 / T38)
        // ------------------------------------------------------------

        // T38 — 异常清单(支持紧迫度/规则/责任人/状态/批次筛选 + 分页)
        listAnomalies: (params = {}) =>
            apiClient.get("/v3/settlement/anomalies", { params }),

        // T38 — 忽略异常(必填理由)
        ignoreAnomaly: (anomalyId, reason) =>
            apiClient.post(`/v3/settlement/anomalies/${anomalyId}/ignore`, { reason }),

        // 2026-05-13 决策 13 — 撤回忽略(IGNORED → OPEN,note 选填)
        // 复用 reactivated_count 字段累加,保留 ignored_at/by/reason 审计痕迹
        unignoreAnomaly: (anomalyId, note = null) =>
            apiClient.post(`/v3/settlement/anomalies/${anomalyId}/unignore`, { note }),

        // T38 — 手动触发扫描(scope: batch | flight | all)
        // 全量扫描需要逐批次跑规则,远超 axios 全局 30 秒超时,单独放宽到 5 分钟;
        // 否则"立即扫描"必然在前端报 timeout,而后端实际仍在扫,用户重试还会叠加并发扫描
        triggerAnomalyScan: ({ scope, batchId = null, flightOperationId = null }) =>
            apiClient.post(
                "/v3/settlement/anomalies/scan",
                {
                    scope,
                    batch_id: batchId,
                    flight_operation_id: flightOperationId,
                },
                { timeout: 300000 }
            ),

        // T38 — 规则配置列表
        listAnomalyRuleConfigs: () =>
            apiClient.get("/v3/settlement/anomaly-rules"),

        // T38 — 更新规则阈值(仅 settlement:rule:admin)
        updateAnomalyRuleConfig: (ruleId, payload) =>
            apiClient.patch(`/v3/settlement/anomaly-rules/${ruleId}`, payload),

        // ------------------------------------------------------------
        // 2026-05-13 — 结算关注飞机配置(LegCostFilter 弹窗用)
        //   挂在 dataQuality 命名空间下,与其它 S1-S5 数据可信化 API 同级
        //   - 列出所有飞机 + is_tracked 状态(未配置视为 tracked)
        //   - 批量更新 is_tracked(全屏蔽语义:FALSE 的飞机连账单都不显示)
        // ------------------------------------------------------------

        // 飞机跟踪状态列表
        listAircraftTracking: () =>
            apiClient.get("/v3/settlement/aircraft-tracking"),

        // 批量更新飞机跟踪状态(items: [{aircraft_id, is_tracked}])
        bulkUpdateAircraftTracking: (items) =>
            apiClient.put("/v3/settlement/aircraft-tracking", { items }),

        // ------------------------------------------------------------
        // S5 永久档案(T53 / T54 / T55)
        // ------------------------------------------------------------

        // T53 — 读历史档案(version 不传取最新)
        getProfitReportSnapshot: (batchId, flightOperationId, costType, version = null) => {
            const params = version ? { version } : {};
            return apiClient.get(
                `/v3/settlement/profit-reports/${batchId}/snapshot/${flightOperationId}/${costType}`,
                { params }
            );
        },

        // T55 — 列出某批次所有 snapshot 版本(用于版本切换 + 漂移对比)
        listProfitReportSnapshotVersions: (batchId) =>
            apiClient.get(`/v3/settlement/profit-reports/${batchId}/snapshot-versions`),
    },

    /**
     * 航线实例分析相关接口（利润报表管理页面使用）
     */
    routeInstances: {
        // 获取航线实例列表
        getRouteInstances: (params) => apiClient.get("/v1/settlement/profit-reports/route-instances", { params }),

        // 获取航线实例详情
        getRouteInstanceDetail: (routeInstanceId) => apiClient.get(`/v1/settlement/profit-reports/route-instances/${routeInstanceId}`),

        // 获取航线实例汇总统计
        getRouteInstancesSummary: (params) => apiClient.get("/v1/settlement/profit-reports/route-instances/summary", { params }),

        // 获取飞机号列表
        getAircraftList: () => apiClient.get("/v1/settlement/profit-reports/aircraft-list"),

        // 获取筛选条件双向联动可选集合（飞机号→航线 / 航线→飞机号）
        getFilterOptions: (params) => apiClient.get("/v1/settlement/profit-reports/filter-options", { params }),

        // 获取除冰统计数据
        getDeicingStatistics: (params) => apiClient.get("/v1/settlement/profit-reports/deicing-statistics", { params }),

        // 获取BH/FH飞行小时统计数据
        getBhFhStatistics: (params) => apiClient.get("/v1/settlement/profit-reports/bh-fh-statistics", { params }),

        // 获取BH/FH时间序列数据（用于可视化图表）
        getBhFhTimeSeries: (params) => apiClient.get("/v1/settlement/profit-reports/bh-fh-time-series", { params }),

        // 获取燃油时间序列数据（用于可视化图表，按航段维度）
        getFuelTimeSeries: (params) => apiClient.get("/v1/settlement/profit-reports/fuel-time-series", { params }),

        // 获取统计看板所有统计数据（性能优化版本 - 一次性返回summary、deicing、bh_fh）
        getDashboardStatistics: (params) => apiClient.get("/v1/settlement/profit-reports/dashboard-statistics", { params }),

    },

    /**
     * FOP视角燃油覆盖统计
     */
    fuelCoverage: {
        getCoverage: (params) => apiClient.get("/v1/settlement/fuel-coverage", { params }),
        getFilterOptions: (params) => apiClient.get("/v1/settlement/fuel-coverage/filter-options", { params }),
    },

    /**
     * 公共费用分摊
     */
    costAllocation: {
        // 获取可分摊的目标航段列表
        getAllocationTargetLegs: (params) => apiClient.get("/v1/settlement/allocation-target-legs", { params }),

        // 验证选中航段的汇率有效性
        validateAllocationRates: (data) => apiClient.post("/v1/settlement/validate-allocation-rates", data),

        precheckAllocationEntry: (data) =>
            apiClient.post("/v1/settlement/cost-allocation-precheck", data),

        // 创建费用分摊录入
        createAllocationEntry: (data) => apiClient.post("/v1/settlement/cost-allocation-entry", data),
    },

    /**
     * 汇率相关
     */
    exchangeRate: {
        // 获取汇率 - 使用结算系统专用接口
        // targetDate(可选, YYYY-MM-DD): 按该日期所处的汇率生效区间取汇率; 不传则取当前有效汇率
        getCurrentRate: (fromCurrency, toCurrency = "USD", targetDate) =>
            apiClient.get(`/v1/settlement/exchange-rate/${fromCurrency}/${toCurrency}`, {
                params: targetDate ? { target_date: targetDate } : undefined,
            }),
    },

    /**
     * ACMI费用录入
     */
    acmi: {
        // 创建ACMI费用录入（旧方法，保留兼容性）
        createEntry: (data) => apiClient.post("/v1/settlement/acmi-entry", data),

        // 获取ACMI费用记录列表（基于BH数据）
        getRecords: (params) => apiClient.get("/v1/settlement/acmi-records", { params }),

        // 保存ACMI费用（批量基于BH计算）- 参照旧系统SimpleCostEntryRequest
        saveCost: (data) => apiClient.post("/v1/settlement/acmi/save-cost", data),
    },

    /**
     * ACMI 调整单据。入账后另加一笔，不覆盖原录入。
     */
    acmiAdjustment: {
        aircraft: () => apiClient.get("/v3/settlement/acmi-adjustments/aircraft"),
        workspace: (params) => apiClient.get("/v3/settlement/acmi-adjustments", { params }),
        get: (id) => apiClient.get(`/v3/settlement/acmi-adjustments/${id}`),
        create: (data) => apiClient.post("/v3/settlement/acmi-adjustments", data),
        update: (id, data) => apiClient.put(`/v3/settlement/acmi-adjustments/${id}`, data),
        post: (id, data) => apiClient.post(`/v3/settlement/acmi-adjustments/${id}/post`, data),
        withdraw: (id) => apiClient.post(`/v3/settlement/acmi-adjustments/${id}/withdraw`),
        abandon: (id) => apiClient.post(`/v3/settlement/acmi-adjustments/${id}/abandon`),
    },

    /**
     * Commission费用录入
     */
    commission: {
        // 创建Commission费用录入（旧方法，保留兼容性）
        createEntry: (data) => apiClient.post("/v1/settlement/commission-entry", data),

        // 获取Commission费用记录列表（基于BH数据）
        getRecords: (params) => apiClient.get("/v1/settlement/commission-records", { params }),

        // 保存Commission费用（批量基于BH计算）- 参照旧系统SimpleCostEntryRequest
        saveCost: (data) => apiClient.post("/v1/settlement/commission/save-cost", data),
    },

    /**
     * 结算工作台 API
     * - 根据行动项ID加载结算上下文（账单 + 分节 + 行项目）
     * - 后端返回的时间字段保持UTC，前端展示再做时区转换（由增强模块处理）
     *
     * 约定返回结构（示例）：
     * {
     *   invoice_number: string,
     *   invoice_date: string, // UTC 日期或日期时间
     *   currency: string,
     *   grand_total: number,
     *   remarks?: string,
     *   sections: Array<{
     *     section_identifier: string,
     *     sub_total: number,
     *     line_items: Array<{
     *       item_id: string,
     *       description: string,
     *       quantity?: number,
     *       unit_price?: number,
     *       total_price?: number,
     *       allocation_type?: "DIRECT" | "ALLOCATABLE",
     *       match_confidence?: "HIGH" | "MEDIUM" | "LOW",
     *       matched_flight_id?: number,
     *       anomalies?: string[],
     *       additional_info?: object
     *     }>
     *   }>
     * }
     */

    /**
     * 使用 AP Invoice 修复 Agent 异步修正账单结构
     * 后端会立即返回，Agent 在后台执行
     * @param {Object} data { invoice_data_source_id, invoice_index, section_identifier, user_feedback }
     * @returns {Promise<import('axios').AxiosResponse>} 返回 { success, message }
     */
    fixAPInvoice: async (data) => {
        return await apiClient.post(`/v1/settlement/ap-reconciliation/fix-ap-invoice`, data);
    },

    /**
     * 账单数据源 - 列表查询（分页）
     * @param {Object} params { page, page_size, status, source_type, is_active_parser, parser_version, search, sort_by, sort_order }
     */
    listInvoiceDataSources: async (params = {}) => {
        return await apiClient.get(`/v1/settlement/invoice-data-sources`, { params });
    },

    /**
     * 检查文件是否重复
     * @param {string} filename 文件名
     */
    checkDuplicateFile: async (filename) => {
        return await apiClient.get(`/v1/settlement/invoice-data-sources/check-duplicate`, { params: { filename } });
    },

    /**
     * 获取账单数据源状态统计
     */
    getInvoiceDataSourceStats: async () => {
        return await apiClient.get(`/v1/settlement/invoice-data-sources/stats`);
    },

    /**
     * 获取已审核费用统计
     * @param {Object} params { year, month }
     */
    getAuditedExpensesStats: async (params) => {
        return await apiClient.get(`/v1/settlement/stats/audited-expenses`, { params });
    },

    /**
     * 获取销售航线盈利统计
     * @param {Object} params { limit }
     */
    getSalesRouteProfitabilityStats: async (params) => {
        return await apiClient.get(`/v1/settlement/stats/sales-route-profitability`, { params });
    },

    /** 获取单个数据源详情 */
    getInvoiceDataSourceById: async (id) => {
        return await apiClient.get(`/v1/settlement/invoice-data-sources/${id}`);
    },

    /** 创建单个数据源（一般不直接用，批量注册更常用） */
    createInvoiceDataSource: async (data) => {
        return await apiClient.post(`/v1/settlement/invoice-data-sources`, data);
    },

    /** 更新数据源（支持 structured_data 手动编辑） */
    updateInvoiceDataSource: async ({ id, data }) => {
        return await apiClient.put(`/v1/settlement/invoice-data-sources/${id}`, data);
    },

    /** 删除数据源 */
    deleteInvoiceDataSource: async (id) => {
        return await apiClient.delete(`/v1/settlement/invoice-data-sources/${id}`);
    },

    /** 批量删除数据源，后端逐条执行运行态停止与 CONFIRMED 保护 */
    batchDeleteInvoiceDataSources: async (ids) => {
        return await apiClient.delete(`/v1/settlement/invoice-data-sources/batch`, {
            data: { ids },
        });
    },

    /** 批量注册上传的文件为数据源（并可选择立即触发解析） */
    batchRegisterInvoiceDataSources: async (data) => {
        // data: { vendor_id, standard_cost_type, files, parse_immediately?, parser_instructions?, batch_number?, high_resolution_pdf_ocr? }
        return await apiClient.post(`/v1/settlement/invoice-data-sources/batch-register`, data);
    },

    /** 获取航班费用验证结果 */
    getValidationResult: async (id) => {
        return await apiClient.get(`/v1/settlement/invoice-data-sources/${id}/validation-result`);
    },

    /** @deprecated 旧触发端点，前端新代码请改用 apInvoicePipelineApi.retryFromStage(id, "ocr") */
    parseInvoiceDataSource: async (id, data = {}) => {
        // data: { instructions?: string }
        return await apiClient.post(`/v1/settlement/invoice-data-sources/${id}/parse`, data);
    },

    /** @deprecated 旧触发端点，前端新代码请改用 apInvoicePipelineApi.retryFromStage(id, "parser") */
    triggerAiSettlement: async (id, data = {}) => {
        // data: { extra_prompt?: string }
        return await apiClient.post(`/v1/settlement/invoice-data-sources/${id}/ai-settlement`, data);
    },

    /** @deprecated 旧触发端点，前端新代码请改用 apInvoicePipelineApi.retryFromStage(id, "allocation") */
    triggerCostAllocation: async (id, data = {}) => {
        // data: { extra_prompt?: string }
        return await apiClient.post(`/v1/settlement/invoice-data-sources/${id}/cost-allocation`, data);
    },

    /** @deprecated 旧触发端点，前端新代码请改用 apInvoicePipelineApi.retryFromStage(id, "validation") */
    triggerValidation: async (id, data = {}) => {
        // data: { extra_prompt?: string }
        return await apiClient.post(`/v1/settlement/invoice-data-sources/${id}/validation`, data);
    },


    /** @deprecated 旧触发端点，前端新代码请改用 apInvoicePipelineApi.stopPipeline(id) */
    stopInvoiceDataSourceProcessing: async (id) => {
        return await apiClient.post(`/v1/settlement/invoice-data-sources/${id}/stop`);
    },

    /**
     * 获取处理历史记录 (V5.0)
     * @param {string} id 账单数据源ID
     * @param {boolean} includeDebug 是否包含调试信息（开发阶段使用）
     */
    getProcessingHistory: async (id, includeDebug = false) => {
        return await apiClient.get(`/v1/settlement/invoice-data-sources/${id}/processing-history`, {
            params: { include_debug: includeDebug },
        });
    },

    /**
     * 提交账单 (Commit)
     * @param {Object} data { action_item_id, ignore_warnings, overwrite_existing }
     */
    commitInvoice: async (data) => {
        return await apiClient.post("/v1/settlement/ap/commit", data);
    },

    /**
     * 拒绝账单 (Reject)
     * @param {Object} data { action_item_id, reason }
     */
    rejectInvoice: async (data) => {
        return await apiClient.post("/v1/settlement/ap/reject", data);
    },

    /**
     * 批次管理 (按 batch_number 聚合)
     */

    /** 获取批次聚合列表 */
    listBatches: async (params = {}) => {
        return await apiClient.get(`/v1/settlement/ap/batches`, { params });
    },

    /** 生成批次号 */
    generateBatchNumber: async () => {
        return await apiClient.post(`/v1/settlement/ap/batches/generate-number`);
    },

    /** 获取批次创建者用户列表（admin 筛选下拉） */
    getBatchCreators: async () => {
        return await apiClient.get(`/v1/settlement/ap/batches/creators`);
    },

    /** 批量修改IDS批次号（含三元组一致性校验 + 重复检测） */
    batchUpdateBatchNumber: async (data) => {
        // data: { ids: string[], batch_number: string, force_update: boolean }
        return await apiClient.post(`/v1/settlement/ap/batches/batch-update-batch-number`, data);
    },

    /**
     * 账单管理 (已入库的正式账单)
     */
    invoices: {
        // 获取账单列表
        getInvoices: (params) => apiClient.get("/v1/settlement/invoices", { params }),

        // 获取单个账单详情
        getInvoiceDetail: (id) => apiClient.get(`/v1/settlement/invoices/${id}`),
    },

    /**
     * 门户发布记录管理 (V4.9 新增 - 合作方账单共享)
     */
    portalPublication: {
        // 批量创建发布记录（指定合作方）
        batchCreate: (data) => apiClient.post("/v1/settlement/portal-publications/batch", data),

        // 批量发布
        batchPublish: (data) => apiClient.patch("/v1/settlement/portal-publications/batch-publish", data),

        // 批量撤回
        batchRevoke: (data) => apiClient.patch("/v1/settlement/portal-publications/batch-revoke", data),

        // 查询发布记录列表
        list: (params) => apiClient.get("/v1/settlement/portal-publications/", { params }),

        // 更新单条发布记录
        update: (id, data) => apiClient.patch(`/v1/settlement/portal-publications/${id}`, data),

        // 删除单条发布记录
        delete: (id) => apiClient.delete(`/v1/settlement/portal-publications/${id}`),

        // 查询 Portal 侧同步状态（跨库查询 flymeta_customer）
        checkSyncStatus: (publicationIds) => apiClient.get("/v1/settlement/portal-publications/check-sync-status", {
            params: { publication_ids: publicationIds.join(",") },
        }),
    },

    /**
     * 飞行记录表管理（Flight Record Log）
     */
    flightRecord: {
        importRecords: async (file, duplicateStrategy = "skip_all", overrideIds = null) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("duplicate_strategy", duplicateStrategy);
            if (overrideIds) {
                formData.append("override_ids", JSON.stringify(overrideIds));
            }
            return apiClient.post("/v1/settlement/flight-records/import", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
        },

        getList: (params) => apiClient.get("/v1/settlement/flight-records", { params }),

        getDetail: (id) => apiClient.get(`/v1/settlement/flight-records/${id}`),

        getBatches: () => apiClient.get("/v1/settlement/flight-records/batches"),

        getAircraftOptions: () => apiClient.get("/v1/settlement/flight-records/aircraft-options"),

        getBatchSummary: (batchId) => apiClient.get(`/v1/settlement/flight-records/batch/${batchId}/summary`),

        rematch: (recordIds) => apiClient.post("/v1/settlement/flight-records/rematch", { record_ids: recordIds }),

        triggerScheduledRematch: () => apiClient.post("/v1/settlement/flight-records/scheduled-rematch"),

        manualLink: (recordId, flightOperationId, force = false) =>
            apiClient.post("/v1/settlement/flight-records/manual-link", {
                record_id: recordId,
                flight_operation_id: flightOperationId,
                force,
            }),

        resolveAmbiguous: (recordId, flightOperationId, force = false) =>
            apiClient.post("/v1/settlement/flight-records/resolve-ambiguous", {
                record_id: recordId,
                flight_operation_id: flightOperationId,
                force,
            }),

        deleteRecords: (recordIds) => apiClient.post("/v1/settlement/flight-records/delete", { record_ids: recordIds }),

        revoke: (recordId) => apiClient.post(`/v1/settlement/flight-records/${recordId}/revoke`),

        exclude: (recordIds) => apiClient.post("/v1/settlement/flight-records/exclude", { record_ids: recordIds }),

        unexclude: (recordId) => apiClient.post(`/v1/settlement/flight-records/${recordId}/unexclude`),

        correctOooiDate: (recordId) => apiClient.post(`/v1/settlement/flight-records/${recordId}/correct-oooi-date`),

        recheckWarnings: () => apiClient.post("/v1/settlement/flight-records/recheck-warnings"),
    },

    /**
     * 燃油报文明细（Fuel Report）
     * 列表与筛选项为 v3 新增能力（v1 已冻结）：GET /api/v3/settlement/fuel-reports
     * 额外返回全筛选范围 aggregates；其余读写端点仍留在 v1。
     */
    fuelReport: {
        getList: (params) => apiClient.get("/v3/settlement/fuel-reports", { params }),
        getFilterOptions: () => apiClient.get("/v3/settlement/fuel-reports/filter-options"),
        update: (id, data) => apiClient.put(`/v1/settlement/fuel-reports/${id}`, data),
        manualMatch: (id, flightOperationId, force = false) => apiClient.put(`/v1/settlement/fuel-reports/${id}/match`, { flight_operation_id: flightOperationId, force }),
        revokeMatch: (id) => apiClient.post(`/v1/settlement/fuel-reports/${id}/revoke`),
        batchReprocess: (dateFrom, dateTo) => apiClient.post('/v1/settlement/fuel-reports/batch-reprocess', { date_from: dateFrom, date_to: dateTo }),
        getReprocessStatus: (taskId) => apiClient.get(`/v1/settlement/fuel-reports/batch-reprocess/${taskId}`),
        deleteRecords: (ids) => apiClient.delete('/v1/settlement/fuel-reports', { data: { ids } }),
        getEmailContent: (rawEmailId) => apiClient.get(`/v1/settlement/fuel-reports/email-content/${rawEmailId}`),
        diagnoseEmails: (params) => apiClient.get('/v1/settlement/fuel-reports/email-diagnostics', { params }),
        reprocessEmail: (rawEmailId) => apiClient.post(`/v1/settlement/fuel-reports/email-diagnostics/${rawEmailId}/reprocess`),
        batchRematch: (payload) => apiClient.post('/v1/settlement/fuel-reports/batch-rematch', Array.isArray(payload) ? { ids: payload } : payload),
        backfillInFuel: () => apiClient.post('/v1/settlement/fuel-reports/backfill-in-fuel'),
        clearReviewReason: (id, code) => apiClient.post(`/v1/settlement/fuel-reports/${id}/clear-review-reason`, { code }),
        approveReview: (id) => apiClient.post(`/v1/settlement/fuel-reports/${id}/approve-review`),
        exportExcel: (params) => apiClient.post('/v1/settlement/fuel-reports/export', params),
    },
};

export default settlementApi;
